/**
 * Order help dialog copy and WhatsApp message builders.
 * Help entry points (list + details) are gated by `shouldShowOrderHelp()` in accountOrdersUtils
 * (subscription orders excluded; max age from REACT_APP_ORDER_HELP_MAX_AGE_DAYS, default 7 days).
 */
import { buildWhatsAppUrl } from '../../../config/whatsapp';
import {
  formatOrderAmountInr,
  formatOrderPlacedAt,
  formatStatusLabel,
  getOrderCardLocation,
  getOrderItemsCommaList,
  getPastOrderStatusCaption,
  orderIsPast,
  orderIsSubscriptionListItem,
} from './accountOrdersUtils';

export const HELP_ISSUE_OTHER_ID = 'other';

/** @type {{ id: string; label: string; messageLine: string }[]} */
export const LIVE_ORDER_HELP_ISSUES = [
  {
    id: 'complete-payment',
    label: 'Payment related',
    messageLine: 'I need help with payment for this order.',
  },
  {
    id: 'order-status',
    label: 'Order status / tracking',
    messageLine: "I'd like an update on my order status.",
  },
  {
    id: 'change-delivery',
    label: 'Change delivery details',
    messageLine: 'I need to change my delivery address or instructions.',
  },
  {
    id: 'cancel-order',
    label: 'Cancel this order',
    messageLine: "I'd like to cancel this order.",
  },
  {
    id: HELP_ISSUE_OTHER_ID,
    label: 'Something else',
    messageLine: '',
  },
];

/** @type {{ id: string; label: string; messageLine: string }[]} */
export const PAST_ORDER_HELP_ISSUES = [
  {
    id: 'wrong-items',
    label: 'Wrong or missing items',
    messageLine: 'There was a problem with the items in this order.',
  },
  {
    id: 'quality',
    label: 'Quality / food issue',
    messageLine: 'I have a quality concern about this order.',
  },
  {
    id: 'refund',
    label: 'Refund or billing',
    messageLine: 'I need help with a refund or billing for this order.',
  },
  {
    id: 'invoice',
    label: 'Invoice / receipt',
    messageLine: 'I need an invoice or receipt for this order.',
  },
  {
    id: HELP_ISSUE_OTHER_ID,
    label: 'Something else',
    messageLine: '',
  },
];

export { orderIsSubscriptionListItem as isSubscriptionOrderRow };

/**
 * @param {{ status: string; placedAt: Date | null; raw: Record<string, unknown> }} order
 * @returns {string}
 */
export function getOrderHelpStatusLabel(order) {
  if (orderIsPast(order)) {
    return getPastOrderStatusCaption(order.status, order.placedAt, order.raw).text;
  }
  return formatStatusLabel(order.status);
}

/**
 * @param {{ displayId: string; status: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }} order
 * @returns {{ id: string; label: string; messageLine: string }[]}
 */
export function getOrderHelpIssueOptions(order) {
  return orderIsPast(order) ? PAST_ORDER_HELP_ISSUES : LIVE_ORDER_HELP_ISSUES;
}

/**
 * @param {{ displayId: string; status: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }} order
 * @param {string} issueId
 * @param {string} otherDetail
 * @returns {string}
 */
export function buildOrderHelpIssueLine(order, issueId, otherDetail) {
  const options = getOrderHelpIssueOptions(order);
  const match = options.find((o) => o.id === issueId);
  if (issueId === HELP_ISSUE_OTHER_ID) {
    const trimmed = String(otherDetail || '').trim();
    return trimmed || 'I need help with this order.';
  }
  return match?.messageLine || 'I need help with this order.';
}

/**
 * @param {{ displayId: string; status: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }} order
 * @param {string} issueLine
 * @returns {string}
 */
export function buildOrderHelpWhatsAppMessage(order, issueLine) {
  const items =
    getOrderItemsCommaList(order.raw, 400) || 'Not available';
  const total = formatOrderAmountInr(order.total) || '—';
  const placed = formatOrderPlacedAt(order.placedAt) || '—';
  const status = getOrderHelpStatusLabel(order);
  const location = getOrderCardLocation(order.raw);
  const lines = [
    'Hi DigiRasa, I need help with my order.',
    '',
    `Order: ${order.displayId}`,
    `Status: ${status}`,
    `Placed: ${placed}`,
    `Items: ${items}`,
    `Total: ${total}`,
  ];
  if (location) {
    lines.push(`Area: ${location}`);
  }
  lines.push('', `I'd like help with: ${issueLine}`);
  return lines.join('\n');
}

/**
 * @param {{ displayId: string; status: string; total: number | null; placedAt: Date | null; raw: Record<string, unknown> }} order
 * @param {string} issueId
 * @param {string} otherDetail
 * @returns {string}
 */
export function buildOrderHelpWhatsAppUrl(order, issueId, otherDetail) {
  const issueLine = buildOrderHelpIssueLine(order, issueId, otherDetail);
  const message = buildOrderHelpWhatsAppMessage(order, issueLine);
  return buildWhatsAppUrl(message);
}

/**
 * @param {string} url
 */
export function openWhatsAppSupport(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
